import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  message,
  Tooltip,
} from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import makeMessager from 'utils/complexFormsMessages';
import { onError } from 'utils/handlers';
import Select from 'components/Select';
import ModalSearch from 'components/Direccion/ModalSearch';
import NumericInput from 'components/NumericInput';
import { selectEstadosGlobales, selectTiposVialidades } from 'store/catalogos';
import { selectCollapsedSidebar } from 'store/app';
import {
  getCodigoPostal,
  getCodigosPostales,
  getColonias,
  getTiposDeVialidades,
} from 'api/catalogos';

export const model = 'direccion';

const Direccion = ({
  useParentID,
  parentID,
  setLoading,
  callback,
  setFormAddress,
  addressPropName,
  setCurrentTabKey,
  // mutable: _mutable,
  mutable,
  permission,
  getParent,
  patchParent,
}) => {
  const dispatch = useDispatch();
  const estadosGlobales = useSelector(selectEstadosGlobales);
  const [formDireccion] = Form.useForm();
  const tiposVialidades = useSelector(selectTiposVialidades);
  const collpasedSidebar = useSelector(selectCollapsedSidebar);
  const [visibleNoSeCodigo, setVisibleNoSeCodigo] = useState(false);
  const [filteredColonies, setFilteredColonies] = useState([]);
  // const [mutable, setMutable] = useState(true);

  const formatValues = (values) => {
    const keys = Object.keys(values);
    const clone = {};
    keys.forEach((prop) => {
      const value = values[prop];
      if (value && value !== 'N/A') {
        clone[prop] = value;
      }
    });
    return clone;
  };

  const getArgs = (values = null) => {
    const args = [];
    if (useParentID && parentID) {
      args.push(parentID);
    }
    if (values) {
      args.push(values);
    }
    return args;
  };

  const getDireccion = async () => {
    setLoading(true);
    formDireccion.resetFields();
    let formattedValues = {};
    if (parentID || !useParentID) {
      const parent = await getParent(...getArgs());
      if (parent) {
        const _address = parent[`${addressPropName}`];
        if (_address?.id) {
          const _codigoPostal = !_address.codigo_postal?.d_codigo
            ? await getCodigoPostal(_address.codigo_postal) : _address.codigo_postal;
          if (_codigoPostal) {
            const [_colonia] = await getColonias({
              d_codigo: _codigoPostal.d_codigo,
            });
            if (_colonia) {
              formattedValues = formatValues({
                ..._address,
                estado: _codigoPostal.municipio.estado.nombre_de_AGEE || undefined,
                municipio: _codigoPostal.municipio.nom_mun || undefined,
                codigo_postal: _codigoPostal.d_codigo,
                colonia: _codigoPostal.id,
              });
              setFilteredColonies(_colonia ? [_colonia] : []);
            }
          }
        }
      }
    }
    // setMutable(_mutable && ((!formattedValues.id && permission?.add)
    //   || (formattedValues.id && permission?.change)));
    setTimeout(() => {
      // eslint-disable-next-line no-use-before-define
      formDireccion.onFinishHandler = onFinishHandler;
      if (setFormAddress) {
        setFormAddress(formDireccion);
      }
    });
    setTimeout(() => {
      formDireccion.setFieldsValue(formattedValues);
    });
    setTimeout(() => {
      setLoading(false);
    });
  };

  const showMsg = makeMessager(setCurrentTabKey, getDireccion);

  const onFinish = async (handleError = true) => {
    try {
      await formDireccion.validateFields();
      setLoading(true);
      const values = formDireccion.getFieldsValue();
      const keys = Object.keys(values);
      const validProps = keys.filter((prop) => values[prop]);
      if (validProps.length > 1) {
        await formDireccion.validateFields();
        if (parentID) {
          const patchValues = {
            [`${addressPropName}`]: {
              ...values,
              codigo_postal: values.colonia,
            },
          };
          const resPatch = await patchParent(...getArgs(patchValues));
          if (resPatch?.status === 200) {
            message.info('Dirección agregada correctamente');
            callback(resPatch.data[addressPropName], handleError);
            if (handleError) {
              await getDireccion();
            }
          }
        }
      }
      setLoading(false);
    } catch (err) {
      if (handleError) {
        onError(err, setLoading);
      }
    }
  };

  const onFinishHandler = async (tabKey = null, _continue = true) => {
    try {
      if (tabKey) {
        showMsg(tabKey);
      } else {
        await onFinish(_continue);
      }
    } catch (err) {
      onError(err, setLoading);
    }
  };

  useEffect(() => {
    getTiposDeVialidades(dispatch, tiposVialidades);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    getDireccion();
    // eslint-disable-next-line
  }, [parentID]);

  const getFromPostalCode = (param) => {
    setLoading(true);
    setTimeout(async () => {
      const _codigosPostales = await getCodigosPostales({ d_codigo: param });
      if (_codigosPostales.length) {
        const [firstItem] = _codigosPostales;
        if (_codigosPostales.length === 1) {
          formDireccion.setFieldsValue({
            colonia: firstItem.id,
            codigo_postal: firstItem.d_codigo,
            estado: firstItem.municipio.estado.nombre_de_AGEE,
            municipio: firstItem.municipio.nom_mun,
          });
          setFilteredColonies([firstItem]);
        } else {
          formDireccion.setFieldsValue({
            pais: 'México',
            codigo_postal: firstItem.d_codigo,
            estado: firstItem.municipio.estado.nombre_de_AGEE,
            municipio: firstItem.municipio.nom_mun,
            colonia: undefined,
          });
          setFilteredColonies(_codigosPostales);
        }
      } else {
        message.info('No se encontro información relacionada al código postal brindado');
        formDireccion.setFieldsValue({
          pais: 'México',
          codigo_postal: undefined,
          estado: undefined,
          municipio: undefined,
          colonia: undefined,
        });
        setFilteredColonies([]);
      }
    }, 300);
    setLoading(false);
  };

  const rules = {
    required: [{ required: true }],
    need_related: [
      {
        validator: async (rule, value) => {
          const relatedMap = {
            entre_calle: 'tipo_de_vialidad_entre',
            tipo_de_vialidad_entre: 'entre_calle',
            y_calle: 'tipo_de_vialidad_y_calle',
            tipo_de_vialidad_y_calle: 'y_calle',
          };
          if (!value && formDireccion.getFieldValue(relatedMap[rule.field])) {
            throw new Error('El campo es requerido');
          }
        },
      },
    ],
  };

  const onFinishSearch = (values) => {
    if (values) {
      formDireccion.setFieldsValue({
        estado: values.municipio.estado.nombre_de_AGEE,
        municipio: values.municipio.nom_mun,
        colonia: values.id,
        codigo_postal: values.d_codigo,
      });
      setFilteredColonies([values]);
    } else {
      message.warn('Seleccione un elemento');
    }
  };

  return (
    <>
      <Col span={24}>
        <Form
          name={addressPropName}
          form={formDireccion}
          layout="vertical"
          initialValues={{ pais: 'México', estados_globales: 1 }}
          onFinish={async () => onFinish()}
          onValuesChange={async (changedValues) => {
            if (changedValues.codigo_postal?.length === 5) {
              getFromPostalCode(changedValues.codigo_postal);
            }
          }}
        >
          <Row gutter={10}>
            <Col xs={24} sm={24} md={16} lg={12}>
              <Form.Item
                label={(
                  <>
                    <span style={{ marginRight: 5 }}>
                      Código Postal
                    </span>
                    {(mutable && (permission?.add || permission?.change)) && (
                      <Tooltip
                        title="Ingresar este campo llenara
                            automáticamente estado, municipio y las colonias disponibles"
                      >
                        <QuestionCircleOutlined className="primary-on-hover" />
                      </Tooltip>
                    )}
                  </>
                )}
                style={{ marginBottom: 0 }}
              >
                <Input.Group compact>
                  <div style={{ width: 200 }}>
                    <NumericInput name="codigo_postal" maxLength={6} disabled={!mutable && (permission?.add || permission?.change)} />
                  </div>
                  <Form.Item>
                    <Button
                      type="link"
                      onClick={() => setVisibleNoSeCodigo(true)}
                      className="primary-on-hover"
                      disabled={!mutable && (permission?.add || permission?.change)}
                    >
                      No sé mi código
                    </Button>
                  </Form.Item>
                </Input.Group>
              </Form.Item>
            </Col>
            <Col span={24} />
            <Col xs={24} sm={24} md={8}>
              <Form.Item
                name="pais"
                label="País"
              >
                <Input disabled />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8}>
              <Form.Item
                name="estado"
                label="Estado"
              >
                <Input disabled />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8}>
              <Form.Item
                name="municipio"
                label="Municipio"
              >
                <Input disabled />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8}>
              <Form.Item
                label="Colonia"
                name="colonia"
                rules={rules.required}
              >
                <Select dataSource={filteredColonies} labelProp="d_asenta" disabled={!mutable && (permission?.add || permission?.change)} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8}>
              <Form.Item
                name="numero_exterior"
                label="Número Exterior"
                rules={rules.required}
              >
                <Input
                  maxLength={20}
                  disabled={!mutable && (permission?.add || permission?.change)}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8}>
              <Form.Item
                name="numero_interior"
                label="Número Interior"
              >
                <Input
                  maxLength={20}
                  disabled={!mutable && (permission?.add || permission?.change)}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8} lg={collpasedSidebar ? 8 : 12}>
              <Form.Item noStyle>
                <Input.Group style={{ display: 'flex' }} className="select-input" compact>
                  <Form.Item
                    style={{ width: 150 }}
                    name="tipo_de_vialidad"
                    rules={rules.required}
                    label="Tipo"
                  >
                    <Select
                      dataSource={tiposVialidades}
                      disabled={!mutable && (permission?.add || permission?.change)}
                    />
                  </Form.Item>
                  <Form.Item
                    style={{ width: '100%' }}
                    name="calle_principal"
                    label="Vialidad Principal"
                    rules={rules.required}
                  >
                    <Input
                      maxLength={170}
                      disabled={!mutable && (permission?.add || permission?.change)}
                      allowClear
                    />
                  </Form.Item>
                </Input.Group>
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8} lg={collpasedSidebar ? 8 : 12}>
              <Form.Item noStyle>
                <Input.Group style={{ display: 'flex' }} className="select-input" compact>
                  <Form.Item
                    style={{ width: 150 }}
                    name="tipo_de_vialidad_entre"
                    label="Tipo"
                    rules={rules.need_related}
                  >
                    <Select
                      dataSource={tiposVialidades}
                      disabled={!mutable && (permission?.add || permission?.change)}
                    />
                  </Form.Item>
                  <Form.Item
                    style={{ width: '100%' }}
                    name="entre_calle"
                    rules={rules.need_related}
                    label="Entre Vialidad"
                    className="affix-wrapper-without-padding"
                  >
                    <Input
                      maxLength={170}
                      disabled={!mutable && (permission?.add || permission?.change)}
                      allowClear
                    />
                  </Form.Item>
                </Input.Group>
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8} lg={collpasedSidebar ? 8 : 12}>
              <Form.Item noStyle>
                <Input.Group style={{ display: 'flex' }} className="select-input" compact>
                  <Form.Item
                    // Change heading: style={{ width: 130 }}
                    style={{ width: 150 }}
                    name="tipo_de_vialidad_y_calle"
                    label="Tipo"
                    rules={rules.need_related}
                  >
                    <Select
                      dataSource={tiposVialidades}
                      disabled={!mutable && (permission?.add || permission?.change)}
                    />
                  </Form.Item>
                  <Form.Item
                    style={{ width: '100%' }}
                    name="y_calle"
                    rules={rules.need_related}
                    label="Vialidad"
                  >
                    <Input
                      maxLength={170}
                      disabled={!mutable && (permission?.add || permission?.change)}
                      allowClear
                    />
                  </Form.Item>
                </Input.Group>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="descripcion_de_la_ubicacion"
                label="Descripción de la Ubicación"
              >
                <Input.TextArea
                  autoSize={{ minRows: 2, maxRows: 3 }}
                  maxLength={250}
                  disabled={!mutable && (permission?.add || permission?.change)}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8}>
              <Form.Item
                name="estados_globales"
                label="Estado Global"
                rules={rules.required}
                hasFeedback
                hidden
              >
                <Select
                  disabled
                  dataSource={estadosGlobales}
                />
              </Form.Item>
            </Col>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Row>
        </Form>
      </Col>
      <ModalSearch
        setLoading={setLoading}
        callback={onFinishSearch}
        setVisible={setVisibleNoSeCodigo}
        open={visibleNoSeCodigo}
      />
    </>
  );
};

Direccion.propTypes = {
  parentID: PropTypes.number,
  callback: PropTypes.func,
  getParent: PropTypes.func.isRequired,
  patchParent: PropTypes.func.isRequired,
  addressPropName: PropTypes.string,
  setLoading: PropTypes.func.isRequired,
  setFormAddress: PropTypes.func.isRequired,
  setCurrentTabKey: PropTypes.func.isRequired,
  mutable: PropTypes.bool,
  useParentID: PropTypes.bool,
  permission: PropTypes.shape({
    add: PropTypes.bool.isRequired,
    authorize: PropTypes.bool.isRequired,
    cancel: PropTypes.bool.isRequired,
    change: PropTypes.bool.isRequired,
    delete: PropTypes.bool.isRequired,
    export: PropTypes.bool.isRequired,
    import: PropTypes.bool.isRequired,
    sync: PropTypes.bool.isRequired,
    view: PropTypes.bool.isRequired,
  }).isRequired,
};

Direccion.defaultProps = {
  parentID: null,
  callback: () => { },
  addressPropName: 'direccion',
  mutable: true,
  useParentID: true,
};

export default Direccion;
